/* eslint-disable global-require */
/* eslint-disable @typescript-eslint/no-var-requires */

import React, { Component } from 'react';
import { Provider, Store } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import HTML5Backend from 'react-dnd-html5-backend';
import TouchBackend from 'react-dnd-touch-backend';
import MultiBackend, { TouchTransition } from 'react-dnd-multi-backend';
import { DndProvider } from 'react-dnd';
import { BrowserRouter } from 'react-router-dom';

import ErrorMessage from 'Shared/components/ErrorMessage';
import ErrorBoundary from 'Shared/components/ErrorBoundary/ErrorBoundary';

import App from './App';
import RootErrorPage from './RootErrorPage/RootErrorPage';
import LoadingScreenErrorPage from './LoadingScreenErrorPage/LoadingScreenErrorPage';
import MobileProvider from './MobileProvider/MobileProvider';
import RollbarProvider from '../shared/components/RollbarProvider/RollbarProvider';
import configureStore from '../store';
import RouterLocationListener from './components/RouterLocationListener/RouterLocationListener';
import { NonceProvider } from 'react-select';
import IntercomWrapper from 'Shared/components/IntercomProvider/IntercomWrapper';
import PendoProvider from 'Shared/components/PendoProvider/PendoProvider';
import { LanguageLocale, TranslationsProvider } from 'Shared/contexts/TranslationsContext';

// Use this to figure out why components are rendering. Add:
// YourComponent.whyDidYouRender = true;
// and check the console.
if (process.env.NODE_ENV !== 'production') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);
}

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
    },
    {
      backend: TouchBackend,
      options: { delayTouchStart: 500 },
      transition: TouchTransition,
      skipDispatchOnTransition: true,
    },
  ],
};

type Props = {
  data: {
    app: {
      clientSyncToken?: string;
      piTranslationsCdnUrl?: string;
      piInitDataUrl?: string;
    };
    user?: { id: number; slug: string; email: string; languageLocale: LanguageLocale };
  };
};

export default class Root extends Component<Props> {
  store: Store;

  constructor(props) {
    super(props);
    const { data } = this.props;

    if (!data.app) {
      data.app = {};
    }
    data.app.clientSyncToken = uuidv4();
    this.store = configureStore(data);
  }

  render() {
    const nonce = (window as any).__webpack_nonce__;
    const { data } = this.props;

    return (
      <ErrorBoundary
        errorPage={
          <TranslationsProvider
            languageLocale={data?.user?.languageLocale}
            translationUrl={data.app.piTranslationsCdnUrl}
            initDataUrl={data.app.piInitDataUrl}
          >
            <RootErrorPage />
          </TranslationsProvider>
        }
        loadingScreenErrorPage={<LoadingScreenErrorPage />}
        user={data?.user}
      >
        <Provider store={this.store}>
          <DndProvider backend={MultiBackend} options={HTML5toTouch}>
            <MobileProvider>
              <BrowserRouter>
                <ErrorMessage />
                <RollbarProvider>
                  <PendoProvider>
                    <IntercomWrapper>
                      <NonceProvider nonce={nonce} cacheKey="react-select">
                        <TranslationsProvider
                          languageLocale={data?.user?.languageLocale}
                          translationUrl={data.app.piTranslationsCdnUrl}
                          initDataUrl={data.app.piInitDataUrl}
                        >
                          <App />
                        </TranslationsProvider>
                      </NonceProvider>
                    </IntercomWrapper>
                  </PendoProvider>
                </RollbarProvider>
                <RouterLocationListener />
              </BrowserRouter>
            </MobileProvider>
          </DndProvider>
        </Provider>
      </ErrorBoundary>
    );
  }
}
